import React, { useState, useEffect, useMemo, useCallback } from 'react';
import firebase from 'firebase/app';
import '../../../App.scss';
import 'antd/dist/antd.css';
import { Button, Image, Spin, Typography } from 'antd';
import { Link } from 'react-router-dom';

import CenteredSpinner from '../../../components/CenteredSpinner';
import SignupShell, { signupTextLink } from '../../../components/client/SignupShell';
import TrackerService from '../../../utils/TrackerService';
import { useClient } from '../../../models/useClient';
import { useTeamDoc } from '../../../components/client/useTeamDoc';

const { Text } = Typography;

/**
 * How long to wait for the members-map trigger before going on regardless.
 *
 * Accepting an invite writes the member document; a trigger then adds the client
 * to the team doc's `members` map, and that map is what every team rule reads.
 * Normally about a second. Past this, waiting longer helps nobody — the team page
 * has its own waiting state and explains itself.
 */
export const MEMBERSHIP_WAIT_MS = 15000;

/**
 * Does this client currently have access to the team?
 *
 * Read from the team doc's `members` map, NOT the /members subcollection. That
 * subcollection allows reads only to people already on the team, so querying it
 * was rejected outright for the one visitor this screen exists to serve — an
 * invitee. Rules are not filters: the query fails, it does not come back empty.
 *
 * The two are not the same list, and the difference matters here. The subcollection
 * is the record of everyone who has been on the team, departed members included
 * (kept with isDeleted so managers can still pay out their share). The map is the
 * uid-keyed access list the rules read, and a departure removes the entry. So this
 * answers "is on the team right now", which is what the screen needs — a returning
 * member should be offered the join, not told they are already here.
 */
export function isClientOnTeam(team, clientId) {
    const members = (team && team.members) || {};
    return Object.keys(members).some(
        (memberUid) => members[memberUid] && members[memberUid].memberClientId === clientId,
    );
}

/**
 * Team identity block — logo (or a four-letter fallback), name, description and
 * member count. Was copy-pasted into five render branches at two sizes; the only
 * thing that ever varied between them was the scale.
 */
export function TeamHeader({ team, large = false }) {
    const [logoFailed, setLogoFailed] = useState(false);
    if (!team) return null;

    const box = large ? 80 : 60;
    const initials = team.teamName ? team.teamName.substring(0, 4).toUpperCase() : 'TEAM';
    const memberCount = team.teamNumberOfMembers || 0;

    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: large ? '24px' : '16px',
        }}>
            <div style={{
                width: `${box}px`,
                height: `${box}px`,
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
                flexShrink: 0,
            }}>
                {!logoFailed && team.topLogo ? (
                    <Image
                        src={team.topLogo}
                        alt={team.teamName}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                        preview={false}
                        onError={() => setLogoFailed(true)}
                    />
                ) : (
                    <Text strong style={{ fontSize: large ? '24px' : '20px', color: '#595959' }}>
                        {initials}
                    </Text>
                )}
            </div>
            <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: large ? '20px' : '18px', display: 'block', marginBottom: large ? '8px' : '4px' }}>
                    {team.teamName}
                </Text>
                {team.teamDescription && (
                    <Text style={{ fontSize: '14px', color: '#8c8c8c', display: 'block', lineHeight: '1.5', marginBottom: '4px' }}>
                        {team.teamDescription}
                    </Text>
                )}
                <Text style={{ fontSize: '14px', color: '#595959', display: 'block' }}>
                    {memberCount} {memberCount === 1 ? 'member' : 'members'}
                </Text>
            </div>
        </div>
    );
}

export function memberCountMessage(team) {
    const memberCount = (team && team.teamNumberOfMembers) || 0;
    if (memberCount <= 1) return 'Be the first member to join the new team.';
    if (memberCount <= 5) return 'Be part of the growing team.';
    return `Join ${memberCount} members already part of the team.`;
}

const primaryButton = {
    height: 52, fontSize: 18, fontWeight: 600, borderRadius: 8,
    backgroundColor: '#ff0098', borderColor: '#ff0098',
};

const cancelLink = { ...signupTextLink, color: '#8c8c8c', textDecoration: 'none', display: 'block', textAlign: 'center' };

/**
 * Team invite screen — /client/manage/:clientId/teamInvites/:teamId/:inviteUuid
 *
 * The team invite link is open to anyone holding it, to join or rejoin. Whether a
 * given accept is a rejoin is decided server-side in acceptTeamInvite, by reading
 * the member document. This screen must not guess: a departed member keeps their
 * member document but loses their entry in the access map, so the one signal that
 * would answer the question is the one they can no longer read. Guessing wrong is
 * destructive — the first-join branch is a set(), which replaces the record.
 *
 * Both the client and the team are live subscriptions. The screen used to read
 * each once at mount, which meant the members map it held was a snapshot from
 * before the join — so it handed people to the team page before they had access
 * there, and they were told the team wasn't available to them.
 */
export default function TeamInviteScreen({ match, history }) {
    const { clientId, teamId, inviteUuid } = match.params;

    const [clientLoading, client] = useClient(clientId);
    const { team, loaded: teamLoaded } = useTeamDoc(teamId);

    const [accepting, setAccepting] = useState(false);
    const [waitedTooLong, setWaitedTooLong] = useState(false);

    const isMember = useMemo(() => isClientOnTeam(team, clientId), [team, clientId]);
    const isLiveTeam = !!(team && team.status === 'live');

    const teamRoute = `/client/manage/${clientId}/teams/${teamId}`;
    const dashboardRoute = `/client/manage/${clientId}`;

    // One subscription serves both jobs: showing the team, and knowing when the
    // trigger has added this client to it. No second listener, no polling.
    useEffect(() => {
        if (!accepting || !isMember) return;
        history.push(teamRoute, { justJoined: true });
    }, [accepting, isMember, history, teamRoute]);

    useEffect(() => {
        if (!accepting || isMember) return undefined;
        const timer = setTimeout(() => setWaitedTooLong(true), MEMBERSHIP_WAIT_MS);
        return () => clearTimeout(timer);
    }, [accepting, isMember]);

    // A stalled trigger should delay someone, not strand them. The team page keeps
    // its own waiting state for the gap.
    useEffect(() => {
        if (!waitedTooLong) return;
        history.push(teamRoute, { justJoined: true });
    }, [waitedTooLong, history, teamRoute]);

    const trackViewed = useCallback((viewedTeam, alreadyMember) => {
        const tracker = new TrackerService({});
        const data = {
            teamId,
            teamName: viewedTeam && viewedTeam.teamName,
            teamClientId: viewedTeam && viewedTeam.teamClientId,
            clientId,
            inviteUuid,
            alreadyMember,
        };
        tracker.mixpanelTrack('team_join_invite_viewed', data);
        if (!tracker.state.isTest) tracker.googleTrack('team_join_invite_viewed', data);
    }, [teamId, clientId, inviteUuid]);

    useEffect(() => {
        if (!teamLoaded || !isLiveTeam) return;
        trackViewed(team, isMember);
        // Deps are deliberately narrow: this fires once per team load, not on every
        // membership change. Including isMember would emit a second "viewed" event
        // the moment the trigger lands, inflating the funnel with one view per join.
    }, [teamLoaded, isLiveTeam]); // eslint-disable-line

    const onClickConfirm = useCallback(async () => {
        const tracker = new TrackerService({});
        const data = {
            teamId,
            teamName: team && team.teamName,
            teamClientId: team && team.teamClientId,
            teamMemberClientId: clientId,
            teamMemberDisplayName: client && client.displayName,
            inviteUuid,
        };
        tracker.mixpanelTrack('accept_team_invite', data);
        if (!tracker.state.isTest) tracker.googleTrack('accept_team_invite', data);

        setAccepting(true);
        try {
            const acceptTeamInvite = firebase.functions()
                .httpsCallable('teamService-teamHandler-acceptTeamInvite');
            await acceptTeamInvite({
                teamId,
                clientId,
                clientDisplayName: client && client.displayName,
                clientEmail: client && client.email,
                clientAvatar: client && client.avatar,
                inviteUuid,
            });
            // Nothing to navigate to yet — the effect above goes when the members
            // map names this client.
        } catch (error) {
            setAccepting(false);
            window.alert('Error: Invite not valid for your account');
        }
    }, [teamId, clientId, team, client, inviteUuid]);

    if (accepting) {
        return (
            <SignupShell title="You're on the team" lead={`Setting up ${(team && team.teamName) || 'your team'}. This takes a few seconds.`}>
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <Spin size="large" />
                </div>
            </SignupShell>
        );
    }

    if (clientLoading || !teamLoaded) {
        return <CenteredSpinner loading />;
    }

    if (!team || !isLiveTeam) {
        return (
            <SignupShell title="Invalid join link" lead="This invite link is not live. Ask your manager for a new one.">
                <Link to={dashboardRoute}><Button type="primary" block style={primaryButton}>Back to dashboard</Button></Link>
            </SignupShell>
        );
    }

    if (isMember) {
        return (
            <SignupShell title="Already on the team" lead={`You're already a member of ${team.teamName}.`}>
                <TeamHeader team={team} large />
                <Link to={teamRoute}><Button type="primary" block style={primaryButton}>Go to team</Button></Link>
                <Link to={dashboardRoute} style={cancelLink}>Back to dashboard</Link>
            </SignupShell>
        );
    }

    // One branch for first-time joiners and returning members alike. The screen
    // cannot tell them apart — a removed member is not in the map and their member
    // document is not readable by them — and it does not need to: the server reads
    // the document and rejoins rather than recreates.
    // Same frame as the sign-up that brought most people here: the step line,
    // the team, one sentence, one button.
    return (
        <SignupShell
            step={3}
            of={3}
            title={`Join ${team.teamName}`}
            lead={`Join the team at ${team.teamName}. Tips are paid straight into your Striketip account. ${memberCountMessage(team)}`}
        >
            <TeamHeader team={team} large />
            <Button type="primary" block disabled={!client} onClick={onClickConfirm} style={primaryButton}>
                {`Join ${team.teamName}`}
            </Button>
            <Link to={dashboardRoute} style={cancelLink}>Cancel</Link>
        </SignupShell>
    );
}
